/**
 * Today lens — pure membership engine (V2_DESIGN_SPEC §1).
 *
 * A task is "today" if ANY of:
 *  - `deadlineAt` resolves to today OR EARLIER (overdue is always today —
 *    hiding overdue work would be a lie), compared by local calendar day;
 *  - it is a Pick for today (§2 — picks arrive as store data; undefined
 *    until V2-B ships);
 *  - it was HANDED OFF (`handedTo`) and its check-back day (`deferredUntil`)
 *    is today or earlier — the "Check in with {name}" moment (Weekly
 *    Insights hand-off, spec §3). Added 18 Oct 2026: until then an arrived
 *    check-back never re-entered Today unless the task also had a due date,
 *    so an undated Zone 3 hand-off came back only under "All" (MEASURED on
 *    the built bundle) — while the guide and a tester message said Today.
 *
 * NOTHING ELSE. The original "work in flight is today" rule (startedAt)
 * was REMOVED on founder field test (Chad, 12 Aug, day of V2-A ship):
 * started tasks dated NEXT WEEK leaked into Today and made the lens feel
 * dishonest. Today is strictly date semantics — undated and future work,
 * started or not, lives under "All" (or gets Picked, in V2-B).
 *
 * Free-text `deadlineHint` without a machine timestamp does NOT qualify:
 * an honest absence beats a guessed inclusion (same contract as date sort).
 *
 * "Not today" deferrals (§3) are excluded even when they match: a task
 * with `deferredUntil` on a FUTURE local day is never today. A deferral
 * that has arrived (today or past) no longer excludes.
 */

import type { Task } from '../types';
import { localDateKey } from './affirmation';

/** Store-level day picks (§2). Synced in V2-B; the lens accepts it now. */
export interface DayPicks {
  /** localDateKey the picks belong to. */
  date: string;
  /** Task ids, at most 3. */
  ids: string[];
}

export interface TodayFilterResult {
  visible: Task[];
  hiddenCount: number;
}

/** True when `epochMs` falls on the same LOCAL calendar day as `todayKey`
 *  or an earlier one. String compare is safe: keys are zero-padded ISO. */
function onOrBefore(epochMs: number, todayKey: string): boolean {
  return localDateKey(new Date(epochMs)) <= todayKey;
}

/** Membership rule for a single task (exported for reuse by Pick 3 UI). */
export function isToday(
  task: Task,
  picks: DayPicks | undefined,
  todayKey: string,
): boolean {
  // §3 exclusion wins over every inclusion rule.
  if (task.deferredUntil !== undefined && !onOrBefore(task.deferredUntil, todayKey)) {
    return false;
  }
  if (task.deadlineAt !== undefined && onOrBefore(task.deadlineAt, todayKey)) {
    return true;
  }
  if (picks && picks.date === todayKey && picks.ids.includes(task.id)) return true;
  // A hand-off whose check-back has arrived comes back to ask about itself.
  if (
    !task.done &&
    Boolean(task.handedTo?.trim()) &&
    task.deferredUntil !== undefined &&
    onOrBefore(task.deferredUntil, todayKey)
  ) {
    return true;
  }
  return false;
}

/**
 * Filter tasks to today's work. Order is preserved — the lens changes
 * which cards show, never how they sort (§1: "just fewer cards").
 */
export function todayFilter(
  tasks: Task[],
  picks: DayPicks | undefined,
  now?: number,
): TodayFilterResult {
  const todayKey = localDateKey(now === undefined ? new Date() : new Date(now));
  const visible = tasks.filter((t) => isToday(t, picks, todayKey));
  return { visible, hiddenCount: tasks.length - visible.length };
}
